import classNames from "classnames";
import styles from "./index.module.scss";
import SocialButtons from "../SocialButtons";
import { Form, Formik } from "formik";
import { initialValues, schema } from "./form";
import FormikInput from "../FormikInput";
import {
  MAX_EMAIL_LENGTH,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
} from "@validation/lengthConstants";
import FormikPassword from "../FormikPassword";
import { Button } from "../Button";
import { toggleSignUpModal } from "@redux/signUp/actions";
import { signIn, signInReset } from "@redux/signIn/actions";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { ErrorCodes } from "@const/errors";
import { toggleBlockedUserModal } from "@redux/blockedUserModal/actions";
import { useService } from "@/base/hooks/useService";
import { ToasterService } from "@services/ToastService";
import { isSWSupported } from "@helpers/sw";
import { notificationPermissions } from "@const/sw";
import { setToken } from "@/firebase";
import { localstorageKeys } from "@const/store";
import { StorageService } from "@services/StorageService";
import { toggleForgotPasswordModal } from "@redux/forgotPassword/actions";
import { useTranslation } from "react-i18next";

/**
 * @param {Object} arguments
 * @param {boolean} arguments.isVisible
 * @param {Function} arguments.closeModal
 * @param {Function} arguments.setEmail
 * @param {Function} arguments.setPassword
 * @param {Function} arguments.setLoginStep
 * */

const SignIn = ({
  closeModal,
  isVisible,
  setEmail: setPropsEmail,
  setPassword,
  setLoginStep,
}) => {
  const { t } = useTranslation("sign-in");
  const { t: t1 } = useTranslation("common");
  const { t: t2 } = useTranslation("errors");
  const { t: t3 } = useTranslation("buttons");
  const storageService = useService(StorageService);
  const dispatch = useDispatch();
  const toastService = useService(ToasterService);
  const [email, setEmail] = useState();

  const { isLoading, error, isSuccess } = useSelector(
    ({ signInReducer }) => signInReducer,
  );

  const { isPushNotificationsAllowed, is2FAEnabled } = useSelector(
    ({ profileReducer }) => profileReducer,
  );

  const handleSubmit = ({ email, password }) => {
    if (!email || !password) return;
    setEmail(encodeURIComponent(email));
    setPropsEmail(email);
    setPassword(password);
    dispatch(signIn({ email: email.trim(), password }));
  };

  const openSignUpModal = () => {
    closeModal();
    dispatch(toggleSignUpModal(true));
  };

  useEffect(() => {
    if (!isSWSupported()) return;
    if (Notification.permission === notificationPermissions.denied) return;
    if (!isPushNotificationsAllowed) return;
    if (!isSuccess) return;
    if (is2FAEnabled) return;

    setToken();
  }, [isSuccess, isPushNotificationsAllowed, is2FAEnabled]);

  useEffect(() => {
    switch (error?.code) {
      case ErrorCodes.UNPROCESSABLE_ERROR:
        toastService.error(t2("INVALID_NAME_OR_PWD"));
        break;
      case ErrorCodes.FORBIDDEN_ERROR:
        dispatch(toggleBlockedUserModal(true));
        dispatch(signInReset());
        break;
      case ErrorCodes.EMAIL_NOT_VERIFIED:
        setPropsEmail(error?.email);
        storageService.set(localstorageKeys.sentAt, Date.now());
        setLoginStep(false);
        break;
      default:
        if (error?.code) {
          toastService.error(error?.message);
        }
        break;
    }
  }, [error, email]);

  const forgotPassword = () => {
    closeModal();
    dispatch(toggleForgotPasswordModal(true));
  };

  return (
    <div
      style={{
        display: isVisible ? "block" : "none",
      }}
    >
      <div className="container d-flex flex-column align-items-center justify-content-center h-100">
        <div
          className={classNames("text-center h3 text-primary", styles.title)}
        >
          {t("TITLE")}
        </div>
        <div className={classNames("mx-auto", styles.formWrapper)}>
          <span
            className={classNames(
              "text-tertiary text-body-1 text-align-center d-block",
              styles.withAccountText,
            )}
          >
            {t("WITH_ACCOUNT")}
          </span>
          <SocialButtons />
          <div
            className={classNames(
              "d-flex align-items-center justify-content-center",
              styles.orContainer,
            )}
          >
            <div className={styles.orLine} />
            <div
              className={classNames(
                styles.sectionsDivider,
                "text-center text-tertiary text-body-1",
              )}
            >
              {t1("OR")}
            </div>
            <div className={styles.orLine} />
          </div>
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={schema}
            validateOnMount
          >
            {({ isValid }) => (
              <Form className="d-flex flex-column">
                <FormikInput
                  maxLength={MAX_EMAIL_LENGTH}
                  label="EMAIL"
                  placeholder="EMAIL"
                  name="email"
                  autocomplete="email"
                />
                <FormikPassword
                  type="password"
                  label="PASSWORD"
                  name="password"
                  min={MIN_PASSWORD_LENGTH}
                  max={MAX_PASSWORD_LENGTH}
                  placeholder="PASSWORD"
                  maxLength={MAX_PASSWORD_LENGTH}
                />
                <span
                  onClick={forgotPassword}
                  className={classNames(
                    "cursor-pointer ms-auto text-underline text-body-1 text-medium link-primary text-decoration-none ms-1",
                    styles.forgot,
                  )}
                >
                  {t("FORGOT_PWD")}
                </span>
                <Button
                  disabled={!isValid || isLoading}
                  color="primary"
                  type="submit"
                  size="lg"
                  className="w-100"
                >
                  {t3("LOG_IN")}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
        <div
          className={classNames(
            "text-tertiary text-center text-regular text-body-2",
            styles.doNotHaveAccount,
          )}
        >
          {t1("DONT_HAVE_ACC")}{" "}
          <span
            onClick={openSignUpModal}
            className="cursor-pointer text-underline text-medium text-body-semi-bold-1 link-primary text-decoration-none"
          >
            {t3("SIGN_UP")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
